import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getApiUrl } from '../config';

const Thread = ({ openAuthModal, showToast }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [thread, setThread] = useState(null);
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyContent, setReplyContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null); // For nested replies
  const [nestedReplyContent, setNestedReplyContent] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [editChanges, setEditChanges] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    const fetchThread = async () => {
      try {
        const response = await fetch(getApiUrl(`/api/threads/${id}`));
        if (response.ok) {
          const data = await response.json();
          setThread(data.thread);
        }
      } catch (error) {
        console.error('Failed to fetch thread:', error);
      }

      try {
        const repliesResponse = await fetch(getApiUrl(`/api/replies/thread/${id}`));
        if (repliesResponse.ok) {
          const repliesData = await repliesResponse.json();
          setReplies(repliesData.replies || []);
        }
      } catch (error) {
        console.error('Failed to fetch replies:', error);
      }

      setLoading(false);
    };
    fetchThread();
  }, [id]);

  const handleVote = async (type) => {
    if (!user) {
      showToast('Sign in to vote');
      return;
    }
    try {
      await fetch(getApiUrl(`/api/threads/${id}/vote`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ vote: type })
      });
    } catch (error) {
      showToast('Failed to vote');
    }
  };

  const handleReplySubmit = async (e, parentReplyId = null, content = null) => {
    e.preventDefault();
    if (!user) {
      showToast('Sign in to reply');
      openAuthModal('signin');
      return;
    }
    
    const replyText = content || replyContent;
    if (!replyText.trim()) return;

    setSubmitting(true);
    try {
      const response = await fetch(getApiUrl('/api/replies'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          threadId: id,
          content: replyText,
          parentReplyId: parentReplyId
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (parentReplyId) {
          // Add nested reply
          setReplies(replies.map(r => {
            if (r._id === parentReplyId) {
              return { ...r, childReplies: [...(r.childReplies || []), data.reply] };
            }
            return r;
          }));
          setReplyingTo(null);
          setNestedReplyContent('');
        } else {
          setReplies([...replies, data.reply]);
          setReplyContent('');
        }
        showToast('Reply posted!');
      }
    } catch (error) {
      showToast('Failed to post reply');
    } finally {
      setSubmitting(false);
    }
  };

  const handleNestedReplySubmit = (e, parentId) => {
    handleReplySubmit(e, parentId, nestedReplyContent);
  };

  const handleEditThread = async (e) => {
    e.preventDefault();
    if (!editContent.trim() || !editChanges.trim()) {
      showToast('Please fill in all fields');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(getApiUrl(`/api/threads/${id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          content: editContent,
          changes: editChanges
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setThread(data.thread);
        setIsEditing(false);
        setEditChanges('');
        showToast('Thread updated successfully!');
      } else {
        const error = await response.json();
        showToast(error.reason || error.error || 'Failed to update thread');
      }
    } catch (error) {
      showToast('Failed to update thread');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteThread = async () => {
    try {
      const response = await fetch(getApiUrl(`/api/threads/${id}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        showToast('Thread deleted');
        navigate('/');
      } else {
        const error = await response.json();
        showToast(error.error || 'Failed to delete thread');
      }
    } catch (error) {
      showToast('Failed to delete thread');
    }
    setShowDeleteConfirm(false);
  };

  const startEditing = () => {
    setEditContent(thread.content);
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditContent('');
    setEditChanges('');
  };

  const isAuthor = user && thread?.author?._id === user._id;

  const getInitials = (name) => {
    if (!name) return 'U';
    const names = name.split(' ');
    if (names.length >= 2) {
      return names[0][0] + names[1][0];
    }
    return name.substring(0, 2).toUpperCase();
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    
    if (hours < 1) return 'just now';
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  const getTagClass = (type) => {
    const classes = {
      question: 'tag-question',
      discussion: 'tag-discussion',
      project: 'tag-project',
      experiment: 'tag-experiment'
    };
    return classes[type] || 'tag-discussion';
  };

  if (loading) {
    return (
      <div className="main-container" style={{ display: 'block' }}>
        <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-tertiary)' }}>
          Loading thread...
        </div>
      </div>
    );
  }

  if (!thread) {
    return (
      <div className="main-container" style={{ display: 'block' }}>
        <div style={{ textAlign: 'center', padding: '60px' }}>
          <h2>Thread not found</h2>
          <Link to="/" style={{ color: 'var(--accent)', marginTop: '16px', display: 'inline-block' }}>
            ← Back to feed
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="main-container" style={{ display: 'block', maxWidth: '800px' }}>
      <Link to="/" style={{ 
        display: 'inline-flex', 
        alignItems: 'center', 
        gap: '8px',
        color: 'var(--text-secondary)', 
        marginBottom: '24px',
        fontSize: '14px'
      }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M19 12H5M12 19l-7-7 7-7"/>
        </svg>
        Back to feed
      </Link>

      <article className="thread-card" style={{ marginBottom: '24px' }}>
        <div className="thread-header">
          <div className="thread-votes">
            <button className="vote-btn" onClick={() => handleVote('up')}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="18 15 12 9 6 15"/>
              </svg>
            </button>
            <span className="vote-count">{thread.voteCount || 0}</span>
            <button className="vote-btn" onClick={() => handleVote('down')}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </button>
          </div>
          <div className="thread-content">
            <div className="thread-meta">
              <span className={`thread-tag ${getTagClass(thread.type)}`}>
                {thread.type?.charAt(0).toUpperCase() + thread.type?.slice(1) || 'Discussion'}
              </span>
              {thread.currentVersion && (
                <span className="thread-version">{thread.currentVersion}</span>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
              <h1 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '16px', lineHeight: '1.4', flex: 1 }}>
                {thread.title}
              </h1>
              {isAuthor && !isEditing && (
                <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                  <button
                    onClick={startEditing}
                    style={{
                      background: 'none',
                      border: '1px solid var(--border)',
                      padding: '6px 12px',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: '13px',
                      color: 'var(--text-secondary)',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                    </svg>
                    Edit
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    style={{
                      background: 'none',
                      border: '1px solid var(--border)',
                      padding: '6px 12px',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: '13px',
                      color: '#ef4444',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="3 6 5 6 21 6"/>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                    </svg>
                    Delete
                  </button>
                </div>
              )}
            </div>
            
            {isEditing ? (
              <form onSubmit={handleEditThread} style={{ marginBottom: '16px' }}>
                <textarea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '14px',
                    borderRadius: 'var(--radius-sm)',
                    border: '1px solid var(--border)',
                    fontSize: '15px',
                    minHeight: '150px',
                    resize: 'vertical',
                    fontFamily: 'inherit',
                    marginBottom: '12px',
                    lineHeight: '1.7'
                  }}
                />
                <input
                  type="text"
                  value={editChanges}
                  onChange={(e) => setEditChanges(e.target.value)}
                  placeholder="Describe your changes (required)..."
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    borderRadius: 'var(--radius-sm)',
                    border: '1px solid var(--border)',
                    fontSize: '14px',
                    marginBottom: '12px'
                  }}
                />
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button type="submit" className="btn btn-primary" disabled={submitting}>
                    {submitting ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button type="button" className="btn btn-ghost" onClick={cancelEditing}>
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <div style={{ fontSize: '15px', color: 'var(--text-secondary)', lineHeight: '1.7', marginBottom: '16px', whiteSpace: 'pre-wrap' }}>
                {thread.content}
              </div>
            )}
            
            {thread.codeBlocks && thread.codeBlocks.map((block, index) => (
              <div className="thread-code-preview" key={index} style={{ maxHeight: 'none' }}>
                <div className="code-preview-header">
                  <span className="code-preview-lang">{block.language || 'code'}</span>
                  {block.filename && <span style={{ color: '#a1a1aa', fontSize: '12px' }}>{block.filename}</span>}
                </div>
                <div className="code-preview-content" style={{ maxHeight: 'none' }}>
                  {block.code}
                </div>
              </div>
            ))}
            
            <div className="thread-footer" style={{ marginTop: '20px' }}>
              <div className="thread-author">
                <div className="author-avatar">
                  {getInitials(thread.author?.username)}
                </div>
                <div className="author-info">
                  <span className="author-name">{thread.author?.username || 'Anonymous'}</span>
                </div>
                <span className="thread-time">• {formatTime(thread.createdAt)}</span>
              </div>
            </div>
          </div>
        </div>
      </article>

      <div style={{ marginBottom: '24px' }}>
        <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>
          {replies.length} {replies.length === 1 ? 'Reply' : 'Replies'}
        </h3>

        {user ? (
          <form onSubmit={handleReplySubmit} style={{ marginBottom: '24px' }}>
            <textarea
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              placeholder="Write your reply..."
              style={{
                width: '100%',
                padding: '16px',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border)',
                fontSize: '14px',
                minHeight: '120px',
                resize: 'vertical',
                fontFamily: 'inherit',
                marginBottom: '12px'
              }}
            />
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={submitting || !replyContent.trim()}
            >
              {submitting ? 'Posting...' : 'Post Reply'}
            </button>
          </form>
        ) : (
          <div style={{
            padding: '24px',
            background: 'var(--bg-secondary)',
            borderRadius: 'var(--radius)',
            textAlign: 'center',
            marginBottom: '24px'
          }}>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '12px' }}>
              Sign in to join the discussion
            </p>
            <button className="btn btn-primary" onClick={() => openAuthModal('signin')}>
              Sign In
            </button>
          </div>
        )}

        {replies.map(reply => (
          <div key={reply._id} className="thread-card" style={{ marginBottom: '12px' }}>
            <div style={{ display: 'flex', gap: '12px' }}>
              <div className="author-avatar" style={{ width: '36px', height: '36px', flexShrink: 0 }}>
                {getInitials(reply.author?.username)}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <span style={{ fontWeight: '500', fontSize: '14px' }}>
                    {reply.author?.username || 'Anonymous'}
                  </span>
                  <span style={{ color: 'var(--text-tertiary)', fontSize: '12px' }}>
                    {formatTime(reply.createdAt)}
                  </span>
                  {reply.isAccepted && (
                    <span style={{
                      background: 'var(--success-bg)',
                      color: 'var(--success)',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: '600'
                    }}>
                      Accepted
                    </span>
                  )}
                </div>
                <div style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                  {reply.content}
                </div>
                {reply.codeBlocks && reply.codeBlocks.map((block, index) => (
                  <div className="thread-code-preview" key={index} style={{ marginTop: '12px' }}>
                    <div className="code-preview-header">
                      <span className="code-preview-lang">{block.language || 'code'}</span>
                    </div>
                    <div className="code-preview-content">
                      {block.code}
                    </div>
                  </div>
                ))}
                <div style={{ display: 'flex', gap: '16px', marginTop: '12px' }}>
                  <button 
                    onClick={() => setReplyingTo(replyingTo === reply._id ? null : reply._id)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: replyingTo === reply._id ? 'var(--accent)' : 'var(--text-tertiary)',
                      fontSize: '13px',
                      cursor: 'pointer'
                    }}
                  >
                    Reply
                  </button>
                </div>

                {/* Nested reply form */}
                {replyingTo === reply._id && (
                  <form 
                    onSubmit={(e) => handleNestedReplySubmit(e, reply._id)}
                    style={{ marginTop: '12px', paddingLeft: '0' }}
                  >
                    <textarea
                      value={nestedReplyContent}
                      onChange={(e) => setNestedReplyContent(e.target.value)}
                      placeholder={`Reply to ${reply.author?.username || 'Anonymous'}...`}
                      style={{
                        width: '100%',
                        padding: '12px',
                        borderRadius: 'var(--radius-sm)',
                        border: '1px solid var(--border)',
                        fontSize: '14px',
                        minHeight: '80px',
                        resize: 'vertical',
                        fontFamily: 'inherit',
                        marginBottom: '8px'
                      }}
                    />
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button type="submit" className="btn btn-primary" disabled={submitting}>
                        {submitting ? 'Posting...' : 'Reply'}
                      </button>
                      <button 
                        type="button" 
                        className="btn btn-ghost"
                        onClick={() => {
                          setReplyingTo(null);
                          setNestedReplyContent('');
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                )}

                {/* Nested replies (child replies) */}
                {reply.childReplies && reply.childReplies.length > 0 && (
                  <div style={{ marginTop: '16px', paddingLeft: '20px', borderLeft: '2px solid var(--border)' }}>
                    {reply.childReplies.map(childReply => (
                      <div key={childReply._id} style={{ marginBottom: '16px' }}>
                        <div style={{ display: 'flex', gap: '10px' }}>
                          <div className="author-avatar" style={{ width: '28px', height: '28px', flexShrink: 0, fontSize: '11px' }}>
                            {getInitials(childReply.author?.username)}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                              <span style={{ fontWeight: '500', fontSize: '13px' }}>
                                {childReply.author?.username || 'Anonymous'}
                              </span>
                              <span style={{ color: 'var(--text-tertiary)', fontSize: '11px' }}>
                                {formatTime(childReply.createdAt)}
                              </span>
                            </div>
                            <div style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
                              {childReply.content}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div 
          className="modal-overlay active" 
          onClick={() => setShowDeleteConfirm(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--bg-primary)',
              padding: '24px',
              borderRadius: 'var(--radius)',
              maxWidth: '400px',
              width: '90%',
              boxShadow: 'var(--shadow-lg)'
            }}
          >
            <h3 style={{ marginBottom: '12px', fontSize: '18px', fontWeight: '600' }}>
              Delete Thread?
            </h3>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '20px', fontSize: '14px' }}>
              This action cannot be undone. All replies to this thread will also be deleted.
            </p>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button 
                className="btn btn-ghost"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </button>
              <button 
                onClick={handleDeleteThread}
                style={{
                  background: '#ef4444',
                  color: 'white',
                  border: 'none',
                  padding: '10px 16px',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Delete Thread
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Thread;

